import React, { useState } from 'react';
import Header from '../../Layout/Header';
import Footer from '../../Layout/Footer';
import FAQ from './FAQ';
import SeoTagsFooter from './SeoTagsFooter';
import EcommerceVisualCards from './EcommerceVisualCards';

function EcommerceShopping() {
  const [activeTab, setActiveTab] = useState('multivendor');
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('upi');
  const [cartAmount, setCartAmount] = useState(2499);
  const [couponApplied, setCouponApplied] = useState(true);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [orderStatus, setOrderStatus] = useState(null);

  const faqs = [
    {
      question: "What E-Commerce solutions does Mokshiq build?",
      answer: "Mokshiq (powered by Rakle Service Private Limited) builds custom multi-vendor shopping portals, single-store B2C platforms, integrated payment gateways (Payin/Payout API), inventory POS software, and logistics tracking modules."
    },
    {
      question: "Which payment gateways and methods are supported?",
      answer: "We integrate direct UPI Intent (PhonePe, Paytm, Google Pay), Credit/Debit cards, NetBanking, Instant Payouts, Wallet systems, and Cash on Delivery (COD) with automated OTP verification."
    },
    {
      question: "Can I manage multi-vendor sellers with custom commission splits?",
      answer: "Yes! Our Multi-Vendor Marketplace solution features custom seller onboarding dashboards, automated commission split calculation, vendor payout scheduling, and store catalog controls."
    },
    {
      question: "How does Mokshiq prevent fraud in e-commerce transactions?",
      answer: "Our e-commerce suite embeds Verify risk scoring, liveness checks, document forgery detection, and real-time transaction monitoring to block fraudulent orders and chargebacks."
    },
    {
      question: "Are shipping and courier APIs pre-integrated?",
      answer: "Yes, we provide automated order fulfillment tracking, pin code serviceability lookup, shipping label generation, and COD refund reconciliation via Delhivery, Shiprocket, BlueDart, and Dunzo."
    }
  ];

  const handleSimulateCheckout = (e) => {
    e.preventDefault();
    setIsProcessingOrder(true);
    setOrderStatus(null);
    
    setTimeout(() => {
      setIsProcessingOrder(false);
      setOrderStatus({
        order_id: "ORD-MOK" + Math.floor(100000 + Math.random() * 900000),
        status: "SUCCESS",
        amount: couponApplied ? cartAmount - 300 : cartAmount,
        payment_method: selectedPaymentMode.toUpperCase(),
        transaction_ref: "TXN" + Date.now(),
        message: "Order placed successfully with zero-fraud risk score!"
      });
    }, 1200);
  };

  return (
    <div className="page_wrapper">
      <Header />
      <main className="page_content">
        
        {/* HERO SECTION */}
        <section className="page_header text-center bg-dark section_decoration overflow-hidden py-5 position-relative" style={{ backgroundImage: "url('assets/images/backgrounds/page_header_bg_1.webp')", backgroundSize: 'cover' }}>
          <div className="container position-relative" style={{ zIndex: 2 }}>
            <span className="badge bg-warning text-dark px-3 py-2 text-uppercase mb-3 font-weight-bold" style={{ letterSpacing: '1px' }}>
              <i className="fa-solid fa-cart-shopping me-2"></i>E-Commerce Platforms &amp; Shopping APIs
            </span>
            <h1 className="page_title text-white mb-3 fw-bold display-5">
              Next-Gen E-Commerce Websites &amp; Shopping Apps
            </h1>
            <p className="text-white-50 mx-auto fs-5 mb-4" style={{ maxWidth: '800px' }}>
              Scalable multi-vendor marketplaces, instant payment checkout, real-time inventory sync, and AI fraud prevention powered by <strong>Rakle Service Private Limited</strong>.
            </p>

            <div className="d-flex flex-wrap justify-content-center gap-3 mb-4">
              <a href="#demo_simulator" className="btn btn-warning btn-lg fw-bold px-4 shadow">
                <i className="fa-solid fa-bolt me-2"></i>Try Live Checkout Demo
              </a>
              <a href="#features_section" className="btn btn-outline-light btn-lg fw-bold px-4">
                <i className="fa-solid fa-cubes me-2"></i>Explore Core Modules
              </a>
            </div>

            <ul className="breadcrumb_nav unordered_list justify-content-center mt-3">
              <li><a href="/" className="text-white-50">Home</a></li>
              <li><a href="/services" className="text-white-50">Services</a></li>
              <li className="active text-warning">E-Commerce Shopping</li>
            </ul>
          </div>
        </section>

        {/* HERO FEATURE DISPLAY WITH GENERATED 3D IMAGE */}
        <section className="hero_showcase py-5 bg-light border-bottom">
          <div className="container py-4">
            <div className="row align-items-center g-4">
              
              <div className="col-lg-6">
                <div className="pe-lg-3">
                  <span className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill font-weight-bold text-uppercase mb-2" style={{ letterSpacing: '0.5px' }}>
                    <i className="fa-solid fa-rocket me-2"></i>Unified Shopping Solution
                  </span>
                  <h2 className="display-6 font-weight-bold text-dark mb-3">
                    Empowering Multi-Vendor Marketplaces &amp; D2C Brands
                  </h2>
                  <p className="text-muted lead mb-4" style={{ fontSize: '1.05rem', lineHeight: '1.7' }}>
                    From single-store B2C online shops to high-volume multi-vendor shopping portals, Mokshiq provides end-to-end e-commerce infrastructure with integrated payment gateways, automated inventory POS billing, and smart order fulfillment.
                  </p>
                  
                  <div className="row g-3 mb-4">
                    <div className="col-sm-6">
                      <div className="p-3 bg-white rounded-3 border shadow-sm h-100">
                        <i className="fa-solid fa-store text-warning fa-2x mb-2"></i>
                        <h3 className="h6 font-weight-bold text-dark mb-1">Multi-Vendor Dashboard</h3>
                        <p className="small text-muted mb-0">Automated vendor split payouts &amp; commission management.</p>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="p-3 bg-white rounded-3 border shadow-sm h-100">
                        <i className="fa-solid fa-credit-card text-success fa-2x mb-2"></i>
                        <h3 className="h6 font-weight-bold text-dark mb-1">Instant UPI &amp; Cards</h3>
                        <p className="small text-muted mb-0">99.99% payment success rates with instant webhook callbacks.</p>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-shield-check text-success fa-lg"></i>
                      <span className="fw-bold small text-dark">PCI-DSS Compliant</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-bolt text-warning fa-lg"></i>
                      <span className="fw-bold small text-dark">&lt; 200ms Latency</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3D Generated Marketplace Image Container */}
              <div className="col-lg-6">
                <div className="position-relative p-2 bg-white rounded-4 shadow-lg border" style={{ borderColor: '#e2e8f0' }}>
                  <img 
                    src="/assets/images/services/ecommerce_marketplace_platform.png" 
                    alt="E-Commerce Marketplace Platform Ecosystem" 
                    className="img-fluid rounded-4 w-100 object-fit-cover shadow-sm"
                    style={{ maxHeight: '420px' }}
                  />

                  {/* Floating Overlay Badge 1 */}
                  <div className="position-absolute bg-dark text-white p-3 rounded-3 shadow-lg d-flex align-items-center gap-3" style={{ bottom: '20px', left: '20px', zIndex: 5, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(15, 23, 42, 0.95)' }}>
                    <div className="rounded-circle bg-success p-2 text-white d-inline-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                      <i className="fa-solid fa-circle-check fa-lg"></i>
                    </div>
                    <div>
                      <div className="font-weight-bold small text-warning">5M+ Monthly Orders</div>
                      <div className="small text-white-50" style={{ fontSize: '11px' }}>Processed with Zero Downtime</div>
                    </div>
                  </div>

                  {/* Floating Overlay Badge 2 */}
                  <div className="position-absolute bg-white text-dark p-2 px-3 rounded-3 shadow-lg d-flex align-items-center gap-2 border" style={{ top: '20px', right: '20px', zIndex: 5, borderColor: '#cbd5e1' }}>
                    <i className="fa-solid fa-chart-line text-primary"></i>
                    <span className="fw-bold small">+45% Checkout Conversion</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* SECURITY & COMPLIANCE VISUAL CARDS SECTION (RECREATED USER REFERENCE IMAGE) */}
        <section className="security_architecture_section section_space bg-white border-bottom">
          <div className="container">
            <EcommerceVisualCards />
          </div>
        </section>

        {/* FRAUD PREVENTION & RISK SCORE FEATURE SHOWCASE WITH GENERATED 3D IMAGE */}
        <section className="fraud_prevention_showcase py-5" style={{ backgroundColor: '#faf5ff' }}>
          <div className="container py-4">
            <div className="row align-items-center g-4">
              
              <div className="col-lg-6 order-lg-2">
                <span className="badge bg-purple text-white px-3 py-2 text-uppercase mb-2 font-weight-bold" style={{ backgroundColor: '#7c3aed', letterSpacing: '0.5px' }}>
                  <i className="fa-solid fa-user-shield me-2"></i>Smart AI Risk Engine
                </span>
                <h2 className="display-6 font-weight-bold text-dark mb-3">
                  Prevent Order Fraud &amp; RTO (Return to Origin) Charges
                </h2>
                <p className="text-muted mb-4" style={{ fontSize: '1.05rem', lineHeight: '1.7' }}>
                  Fake COD orders and payment chargebacks cost e-commerce businesses millions annually. Mokshiq’s built-in AI risk scoring evaluates buyer liveness, address authenticity, and historical return records before confirming orders.
                </p>

                <div className="d-flex flex-column gap-3 mb-4">
                  <div className="d-flex align-items-start gap-3 p-3 bg-white rounded-3 border shadow-sm">
                    <i className="fa-solid fa-shield-virus text-purple fa-2x mt-1" style={{ color: '#7c3aed' }}></i>
                    <div>
                      <h3 className="h6 font-weight-bold text-dark mb-1">Liveness &amp; Device Fingerprinting</h3>
                      <p className="small text-muted mb-0">Detects suspicious bot orders, VPN proxies, and automated checkout scripts.</p>
                    </div>
                  </div>

                  <div className="d-flex align-items-start gap-3 p-3 bg-white rounded-3 border shadow-sm">
                    <i className="fa-solid fa-file-circle-check text-success fa-2x mt-1"></i>
                    <div>
                      <h3 className="h6 font-weight-bold text-dark mb-1">Document &amp; Address Verification</h3>
                      <p className="small text-muted mb-0">Automated pincode serviceability check with direct courier API validation.</p>
                    </div>
                  </div>
                </div>

              </div>

              <div className="col-lg-6 order-lg-1">
                <div className="p-2 bg-white rounded-4 shadow-lg border" style={{ borderColor: '#e9d5ff' }}>
                  <img 
                    src="/assets/images/services/ecommerce_fraud_prevention.png" 
                    alt="E-Commerce AI Fraud Prevention Illustration" 
                    className="img-fluid rounded-4 w-100 object-fit-cover"
                    style={{ maxHeight: '400px' }}
                  />
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* CORE MODULES & FEATURES TABBED SECTION */}
        <section id="features_section" className="section_space bg-white">
          <div className="container">
            <div className="heading_block text-center mb-5">
              <span className="badge bg-warning text-dark px-3 py-2 text-uppercase mb-2 font-weight-bold" style={{ letterSpacing: '1px' }}>
                Full-Stack E-Commerce Architecture
              </span>
              <h2 className="heading_text text-dark font-weight-bold">End-to-End E-Commerce Building Blocks</h2>
              <p className="heading_description text-muted">Everything you need to launch, scale, and automate online shopping portals.</p>
            </div>

            {/* Navigation Tabs */}
            <div className="d-flex justify-content-center mb-4 overflow-auto pb-2">
              <div className="nav nav-pills bg-light p-2 rounded-4 shadow-sm border" role="tablist">
                <button 
                  className={`nav-link fw-bold px-4 py-2 rounded-3 me-2 ${activeTab === 'multivendor' ? 'bg-dark text-warning active shadow-sm' : 'text-dark'}`}
                  onClick={() => setActiveTab('multivendor')}
                >
                  <i className="fa-solid fa-store me-2"></i>Multi-Vendor Portal
                </button>
                <button 
                  className={`nav-link fw-bold px-4 py-2 rounded-3 me-2 ${activeTab === 'gateway' ? 'bg-dark text-warning active shadow-sm' : 'text-dark'}`}
                  onClick={() => setActiveTab('gateway')}
                >
                  <i className="fa-solid fa-credit-card me-2"></i>Payment Gateway &amp; UPI
                </button>
                <button 
                  className={`nav-link fw-bold px-4 py-2 rounded-3 me-2 ${activeTab === 'pos' ? 'bg-dark text-warning active shadow-sm' : 'text-dark'}`}
                  onClick={() => setActiveTab('pos')}
                >
                  <i className="fa-solid fa-boxes-stacked me-2"></i>Inventory &amp; POS Billing
                </button>
                <button 
                  className={`nav-link fw-bold px-4 py-2 rounded-3 ${activeTab === 'logistics' ? 'bg-dark text-warning active shadow-sm' : 'text-dark'}`}
                  onClick={() => setActiveTab('logistics')}
                >
                  <i className="fa-solid fa-truck-fast me-2"></i>Logistics &amp; Courier API
                </button>
              </div>
            </div>

            {/* Tab Content Display */}
            <div className="p-4 p-lg-5 bg-light rounded-4 border shadow-sm">
              {activeTab === 'multivendor' && (
                <div className="row align-items-center g-4">
                  <div className="col-lg-6">
                    <span className="badge bg-warning text-dark mb-2 font-weight-bold">MARKETPLACE ENGINE</span>
                    <h3 className="h3 font-weight-bold text-dark mb-3">Multi-Vendor Seller Portal &amp; Commission Splitter</h3>
                    <p className="text-muted mb-4">
                      Allow thousands of sellers to register, manage catalog items, process customer orders, and track payouts seamlessly.
                    </p>
                    <ul className="list-unstyled d-flex flex-column gap-2 mb-4">
                      <li className="d-flex align-items-center gap-2"><i className="fa-solid fa-circle-check text-success"></i><span>Individual Vendor Dashboards with sales analytics</span></li>
                      <li className="d-flex align-items-center gap-2"><i className="fa-solid fa-circle-check text-success"></i><span>Automated commission split calculation (e.g. 10% platform fee)</span></li>
                      <li className="d-flex align-items-center gap-2"><i className="fa-solid fa-circle-check text-success"></i><span>Vendor payout queue via Instant Bank Payout API</span></li>
                    </ul>
                  </div>
                  <div className="col-lg-6">
                    <div className="bg-dark text-light p-4 rounded-4 shadow">
                      <h4 className="h6 text-warning mb-3 fw-bold"><i className="fa-solid fa-code me-2"></i>Vendor Split API Payload Example</h4>
                      <pre className="bg-black text-info p-3 rounded font-monospace small mb-0">
                        <code>{`{
  "order_id": "ORD-991823",
  "total_amount": 5000.00,
  "splits": [
    {
      "vendor_id": "VEND_7712",
      "amount": 4500.00,
      "payout_mode": "IMPS_INSTANT"
    },
    {
      "vendor_id": "PLATFORM_MOKSHIQ",
      "amount": 500.00,
      "commission_percent": 10
    }
  ]
}`}</code>
                      </pre>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'gateway' && (
                <div className="row align-items-center g-4">
                  <div className="col-lg-6">
                    <span className="badge bg-success text-white mb-2 font-weight-bold">PAYMENT AGGREGATION</span>
                    <h3 className="h3 font-weight-bold text-dark mb-3">Seamless Payin, UPI Intent &amp; COD Verification</h3>
                    <p className="text-muted mb-4">
                      Accept payments from Razorpay, Cashfree, PhonePe, Paytm, Google Pay, Cards, and NetBanking with single-click UPI intent popups.
                    </p>
                    <ul className="list-unstyled d-flex flex-column gap-2 mb-4">
                      <li className="d-flex align-items-center gap-2"><i className="fa-solid fa-circle-check text-success"></i><span>UPI Intent deep-linking for mobile apps</span></li>
                      <li className="d-flex align-items-center gap-2"><i className="fa-solid fa-circle-check text-success"></i><span>COD OTP confirmation to minimize fake order delivery attempts</span></li>
                      <li className="d-flex align-items-center gap-2"><i className="fa-solid fa-circle-check text-success"></i><span>Instant Webhooks for order status updates</span></li>
                    </ul>
                  </div>
                  <div className="col-lg-6">
                    <div className="bg-dark text-light p-4 rounded-4 shadow">
                      <h4 className="h6 text-success mb-3 fw-bold"><i className="fa-solid fa-shield-check me-2"></i>UPI Intent Request Spec</h4>
                      <pre className="bg-black text-success p-3 rounded font-monospace small mb-0">
                        <code>{`{
  "api_key": "MOKSHIQ_LIVE_KEY_8812",
  "customer_mobile": "+91 7311104571",
  "amount": 2499.00,
  "vpa_app": "PHONEPE",
  "callback_url": "https://mokshiq.in/api/v1/webhook"
}`}</code>
                      </pre>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'pos' && (
                <div className="row align-items-center g-4">
                  <div className="col-lg-6">
                    <span className="badge bg-warning text-dark mb-2 font-weight-bold">SMART BILLING</span>
                    <h3 className="h3 font-weight-bold text-dark mb-3">Inventory Sync &amp; POS Billing Software</h3>
                    <p className="text-muted mb-4">
                      Keep your offline retail stores and online e-commerce website stock levels synchronized in real time with automated GST invoice generation.
                    </p>
                    <ul className="list-unstyled d-flex flex-column gap-2 mb-4">
                      <li className="d-flex align-items-center gap-2"><i className="fa-solid fa-circle-check text-success"></i><span>Barcode scanner &amp; receipt printer hardware integration</span></li>
                      <li className="d-flex align-items-center gap-2"><i className="fa-solid fa-circle-check text-success"></i><span>Automated B2B GST tax invoice PDF download</span></li>
                      <li className="d-flex align-items-center gap-2"><i className="fa-solid fa-circle-check text-success"></i><span>Low-stock alert triggers via WhatsApp &amp; SMS</span></li>
                    </ul>
                  </div>
                  <div className="col-lg-6">
                    <div className="bg-dark text-light p-4 rounded-4 shadow">
                      <h4 className="h6 text-info mb-3 fw-bold"><i className="fa-solid fa-print me-2"></i>GST Invoice Payload</h4>
                      <pre className="bg-black text-warning p-3 rounded font-monospace small mb-0">
                        <code>{`{
  "gstin": "07AAAAA0000A1Z5",
  "invoice_number": "INV-2026-8812",
  "items": [{ "sku": "SHIRT_L", "qty": 2, "cgst": 9, "sgst": 9 }],
  "grand_total": 2948.82
}`}</code>
                      </pre>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'logistics' && (
                <div className="row align-items-center g-4">
                  <div className="col-lg-6">
                    <span className="badge bg-primary text-white mb-2 font-weight-bold">LOGISTICS AUTOMATION</span>
                    <h3 className="h3 font-weight-bold text-dark mb-3">Courier Booking &amp; Order Fulfillment API</h3>
                    <p className="text-muted mb-4">
                      Compare courier rates dynamically across Delhivery, Shiprocket, BlueDart, and Dunzo to pick the fastest and cheapest delivery route.
                    </p>
                    <ul className="list-unstyled d-flex flex-column gap-2 mb-4">
                      <li className="d-flex align-items-center gap-2"><i className="fa-solid fa-circle-check text-success"></i><span>Automated shipping label &amp; manifest generation</span></li>
                      <li className="d-flex align-items-center gap-2"><i className="fa-solid fa-circle-check text-success"></i><span>Live tracking link sent to buyers via SMS &amp; WhatsApp</span></li>
                      <li className="d-flex align-items-center gap-2"><i className="fa-solid fa-circle-check text-success"></i><span>Reverse pickup automation for customer returns</span></li>
                    </ul>
                  </div>
                  <div className="col-lg-6">
                    <div className="bg-dark text-light p-4 rounded-4 shadow">
                      <h4 className="h6 text-primary mb-3 fw-bold"><i className="fa-solid fa-truck-fast me-2"></i>Courier Booking Response</h4>
                      <pre className="bg-black text-info p-3 rounded font-monospace small mb-0">
                        <code>{`{
  "awb_code": "DELHIVERY_7718293",
  "courier_partner": "Delhivery Express",
  "estimated_delivery": "2026-09-02",
  "tracking_url": "https://track.mokshiq.in/DELHIVERY_7718293"
}`}</code>
                      </pre>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </section>

        {/* LIVE CHECKOUT DEMO SIMULATOR */}
        <section id="demo_simulator" className="section_space bg-light border-top">
          <div className="container">
            <div className="heading_block text-center mb-5">
              <span className="badge bg-success text-white px-3 py-2 text-uppercase mb-2 font-weight-bold" style={{ letterSpacing: '1px' }}>
                <i className="fa-solid fa-play me-2"></i>Interactive Sandbox
              </span>
              <h2 className="heading_text text-dark font-weight-bold">Live E-Commerce Checkout Simulator</h2>
              <p className="heading_description text-muted">Test how Mokshiq handles instant payment gateway authorization, coupon application, and fraud score checks.</p>
            </div>

            <div className="row g-4 justify-content-center">
              
              {/* Checkout Form */}
              <div className="col-lg-6">
                <div className="bg-white p-4 p-md-5 rounded-4 shadow border h-100" style={{ borderColor: '#e2e8f0' }}>
                  <h3 className="h5 font-weight-bold text-dark mb-4 pb-2 border-bottom">
                    <i className="fa-solid fa-cart-shopping me-2 text-warning"></i>Store Checkout Preview
                  </h3>
                  
                  <form onSubmit={handleSimulateCheckout}>
                    
                    {/* Item Row */}
                    <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 mb-4 border">
                      <div className="d-flex align-items-center gap-3">
                        <div className="bg-warning text-dark p-2 rounded-3 d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px' }}>
                          <i className="fa-solid fa-bag-shopping fa-lg"></i>
                        </div>
                        <div>
                          <div className="font-weight-bold text-dark">Wireless Noise-Canceling Headphones</div>
                          <div className="small text-muted">SKU: MOK-HP-2026</div>
                        </div>
                      </div>
                      <div className="text-end">
                        <div className="font-weight-bold text-dark">₹{cartAmount}</div>
                        <div className="small text-success">In Stock</div>
                      </div>
                    </div>

                    {/* Promo Code Toggle */}
                    <div className="mb-4">
                      <div className="d-flex align-items-center justify-content-between bg-light p-2 px-3 rounded-3 border">
                        <span className="small font-weight-bold text-dark">Apply Promo Code 'MOKSHIQ300'</span>
                        <div className="form-check form-switch m-0">
                          <input 
                            className="form-check-input cursor-pointer" 
                            type="checkbox" 
                            checked={couponApplied} 
                            onChange={(e) => setCouponApplied(e.target.checked)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Payment Mode Selection */}
                    <div className="mb-4">
                      <label className="form-label font-weight-bold small text-dark">Select Payment Method</label>
                      <div className="row g-2">
                        <div className="col-4">
                          <button 
                            type="button" 
                            className={`btn w-100 py-2 btn-sm fw-bold border ${selectedPaymentMode === 'upi' ? 'btn-warning text-dark' : 'btn-light'}`}
                            onClick={() => setSelectedPaymentMode('upi')}
                          >
                            <i className="fa-solid fa-mobile-retro me-1"></i>UPI Intent
                          </button>
                        </div>
                        <div className="col-4">
                          <button 
                            type="button" 
                            className={`btn w-100 py-2 btn-sm fw-bold border ${selectedPaymentMode === 'card' ? 'btn-warning text-dark' : 'btn-light'}`}
                            onClick={() => setSelectedPaymentMode('card')}
                          >
                            <i className="fa-solid fa-credit-card me-1"></i>Card / NetB
                          </button>
                        </div>
                        <div className="col-4">
                          <button 
                            type="button" 
                            className={`btn w-100 py-2 btn-sm fw-bold border ${selectedPaymentMode === 'cod' ? 'btn-warning text-dark' : 'btn-light'}`}
                            onClick={() => setSelectedPaymentMode('cod')}
                          >
                            <i className="fa-solid fa-hand-holding-dollar me-1"></i>COD OTP
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Price Summary */}
                    <div className="border-top pt-3 mb-4">
                      <div className="d-flex justify-content-between text-muted small mb-1">
                        <span>Subtotal:</span>
                        <span>₹{cartAmount}</span>
                      </div>
                      {couponApplied && (
                        <div className="d-flex justify-content-between text-success small mb-1">
                          <span>Discount (MOKSHIQ300):</span>
                          <span>-₹300</span>
                        </div>
                      )}
                      <div className="d-flex justify-content-between text-muted small mb-2">
                        <span>Fraud Protection &amp; Shipping:</span>
                        <span className="text-success">FREE</span>
                      </div>
                      <div className="d-flex justify-content-between font-weight-bold fs-5 text-dark border-top pt-2">
                        <span>Total Payable:</span>
                        <span className="text-success">₹{couponApplied ? cartAmount - 300 : cartAmount}</span>
                      </div>
                    </div>

                    <button 
                      type="submit" 
                      className="btn btn-warning w-100 py-3 font-weight-bold shadow-sm"
                      disabled={isProcessingOrder}
                    >
                      {isProcessingOrder ? (
                        <span><i className="fa-solid fa-spinner fa-spin me-2"></i>Processing Payment &amp; Risk Scoring...</span>
                      ) : (
                        <span><i className="fa-solid fa-lock me-2"></i>Simulate Instant Checkout</span>
                      )}
                    </button>

                  </form>
                </div>
              </div>

              {/* JSON Live Response Console */}
              <div className="col-lg-6">
                <div className="bg-dark text-light p-4 p-md-5 rounded-4 shadow-lg border h-100 d-flex flex-column justify-content-between">
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom border-secondary">
                      <span className="badge bg-success text-dark fw-bold">API WEBHOOK FEEDBACK</span>
                      <span className="small text-muted font-monospace">Status: 200 OK</span>
                    </div>

                    <h4 className="h6 text-warning mb-3 font-weight-bold">
                      <i className="fa-solid fa-terminal me-2"></i>Live Payment Callback &amp; Risk Payload
                    </h4>

                    {orderStatus ? (
                      <pre className="bg-black text-success p-3 rounded font-monospace small border border-success" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                        <code>{JSON.stringify(orderStatus, null, 2)}</code>
                      </pre>
                    ) : (
                      <div className="bg-black p-4 rounded text-center text-muted border border-secondary font-monospace" style={{ minHeight: '260px' }}>
                        <i className="fa-solid fa-code fa-2x mb-3 text-secondary"></i>
                        <p className="small mb-0">Click 'Simulate Instant Checkout' on the left to fire a live gateway callback payload.</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-3 border-top border-secondary text-muted small">
                    <div className="d-flex align-items-center justify-content-between">
                      <span><i className="fa-solid fa-circle text-success me-1"></i> Sandbox Server Active</span>
                      <span>Mokshiq API v2.4</span>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </section>

        {/* DEVELOPER SANDBOX REQUEST SECTION */}
        <section className="section_space bg-white">
          <div className="container">
            <div className="p-4 p-lg-5 bg-dark text-light rounded-4 shadow-lg position-relative overflow-hidden">
              <div className="row align-items-center">
                <div className="col-lg-7 mb-4 mb-lg-0">
                  <span className="badge bg-warning text-dark px-3 py-2 text-uppercase mb-3 font-weight-bold">
                    Ready to Launch Your Store?
                  </span>
                  <h2 className="display-6 font-weight-bold text-white mb-3">
                    Get Instant Sandbox Credentials &amp; API Keys
                  </h2>
                  <p className="text-white-50 lead mb-0" style={{ fontSize: '1.05rem' }}>
                    Speak with our lead e-commerce architects at <strong>Rakle Service Private Limited</strong> to request Sandbox API keys, custom theme design, or multi-vendor onboarding support.
                  </p>
                </div>
                
                <div className="col-lg-5 text-lg-end">
                  <a href="mailto:sales@mokshiq.in?subject=Ecommerce%20API%20Sandbox%20Request" className="btn btn-warning btn-lg fw-bold px-4 me-2 mb-2 shadow">
                    <i className="fa-solid fa-key me-2"></i>Get Sandbox Key
                  </a>
                  <a href="tel:+917311104571" className="btn btn-outline-light btn-lg fw-bold px-4 mb-2">
                    <i className="fa-solid fa-phone me-2"></i>+91 7311104571
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ SECTION */}
        <FAQ 
          faqList={faqs} 
          title="E-Commerce Platform FAQs" 
          subtitle="Frequently asked questions about building online stores, payment gateways, and multi-vendor marketplaces." 
        />

      </main>
      <Footer />
      <SeoTagsFooter />
    </div>
  );
}

export default EcommerceShopping;
